import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/pages/Login.css";

export default function Login() {
  const [credentials, setCredentials] = useState({ usernameOrEmail: "", password: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const loginInProgressRef = useRef(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
    setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loginInProgressRef.current) return;
    loginInProgressRef.current = true;
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/loginUser`,
        {
          usernameOrEmail: credentials.usernameOrEmail,
          password: credentials.password,
        }
      );

      const token = response.data.token;
      const user = response.data.user;
      const rawRole = user?.role;
      const role = String(rawRole || "").toLowerCase() === "user" ? "orguser" : rawRole;

      if (!token || !role) {
        setError("Invalid response from server");
        setIsLoading(false);
        return;
      }

      sessionStorage.setItem("token", token);
      sessionStorage.setItem("role", role.toLowerCase());
      sessionStorage.setItem("userDetails", JSON.stringify(user));

      switch (role.toLowerCase()) {
        case "superadmin":
          navigate("/super-admin");
          break;
        case "admin":
          navigate("/admin");
          break;
        case "subadmin":
          navigate("/sub-admin");
          break;
        case "orguser":
          navigate("/orguser");
          break;
        default:
          setError("Invalid role");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
      loginInProgressRef.current = false;
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="login-title">Welcome Back</h1>
            <p className="login-subtitle">Sign in to your account to continue</p>
          </div>

          {error && (
            <div className="login-error-alert">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="login-form">
            <div className="login-input-group">
              <label htmlFor="usernameOrEmail" className="login-label">Username or Email</label>
              <div className="login-input-wrapper">
                <svg className="login-input-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <input
                  id="usernameOrEmail"
                  type="text"
                  name="usernameOrEmail"
                  value={credentials.usernameOrEmail}
                  onChange={handleChange}
                  placeholder="Enter your username or email"
                  className="login-input"
                  required
                />
              </div>
            </div>

            <div className="login-input-group">
              <label htmlFor="password" className="login-label">Password</label>
              <div className="login-input-wrapper">
                <svg className="login-input-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="login-input login-input-password"
                  required
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="login-forgot-link">
              <Link to="/forgot-password">Forgot your password?</Link>
            </div>

            <button type="submit" className="login-submit-btn" disabled={isLoading}>
              <span>{isLoading ? "Signing in..." : "Sign In"}</span>
              {!isLoading && (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              )}
            </button>
          </form>

          <div className="login-footer-section">
            {/* Register link removed as requested */}
          </div>
        </div>

        <div className="login-decorative-bg">
          <div className="login-circle login-circle-1"></div>
          <div className="login-circle login-circle-2"></div>
          <div className="login-circle login-circle-3"></div>
        </div>
      </div>
    </div>
  );
}
